import type {
  MoneyState,
  SubCreate,
  SubDraft,
  SubPatch,
  Subscription,
} from "../../shared/money";
import type {
  EnrichRequest,
  EnrichResponse,
  Idea,
  IdeaCreate,
  IdeaPatch,
  LinkPreview,
} from "../../shared/types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("content-type", "application/json");
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep statusText
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  login: (password: string) =>
    req<void>("/api/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => req<void>("/api/logout", { method: "POST" }),
  me: () => req<void>("/api/me"),
  listIdeas: () => req<Idea[]>("/api/items"),
  createIdea: (body: IdeaCreate) =>
    req<Idea>("/api/items", { method: "POST", body: JSON.stringify(body) }),
  patchIdea: (id: string, patch: IdeaPatch) =>
    req<Idea>(`/api/items/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteIdea: (id: string) => req<void>(`/api/items/${id}`, { method: "DELETE" }),
  linkMeta: (url: string) =>
    req<LinkPreview>("/api/meta", { method: "POST", body: JSON.stringify({ url }) }),
  enrich: (body: EnrichRequest) =>
    req<EnrichResponse>("/api/enrich", { method: "POST", body: JSON.stringify(body) }),
  research: (id: string) =>
    req<Idea>("/api/research", { method: "POST", body: JSON.stringify({ id }) }),

  money: () => req<MoneyState>("/api/money"),
  createSub: (body: SubCreate) =>
    req<Subscription>("/api/subs", { method: "POST", body: JSON.stringify(body) }),
  patchSub: (id: string, patch: SubPatch) =>
    req<Subscription>(`/api/subs/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteSub: (id: string) => req<void>(`/api/subs/${id}`, { method: "DELETE" }),
  paySub: (id: string, dueDate: string, skipped = false) =>
    req<MoneyState>(`/api/subs/${id}/pay`, {
      method: "POST",
      body: JSON.stringify({ dueDate, skipped }),
    }),
  unpaySub: (id: string, dueDate: string) =>
    req<MoneyState>(`/api/subs/${id}/unpay`, {
      method: "POST",
      body: JSON.stringify({ dueDate }),
    }),
  parseSub: (text: string) =>
    req<SubDraft>("/api/subs/parse", { method: "POST", body: JSON.stringify({ text }) }),

  listSites: () => req<{ sites: SiteRow[] }>("/api/sites"),
  setSiteRetired: (id: string, retired: boolean) =>
    req<{ id: string; retired: boolean }>(`/api/sites/${id}`, {
      method: "POST",
      body: JSON.stringify({ retired }),
    }),
};

/** One switchable subdomain and whether its kill switch is thrown. */
export interface SiteRow {
  id: string;
  label: string;
  origin: string;
  blurb: string;
  kind: "game" | "tool";
  retired: boolean;
}
